import { useCallback } from "react";
import { useReferralEarningsStore } from "../stores/referralEarningsStore";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { getReferralEarnings } from "../api/rewards/api";
import { ReferralEarning } from "../api/rewards/types";

const PAGE_LIMIT = 20;

interface UseReferralEarningsReturn {
  earnings: ReferralEarning[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  totalXp: number;
  totalReferrals: number;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  fetch: () => Promise<void>;
  reset: () => void;
}

/**
 * Hook for managing referral earnings with pagination
 * Only fetches when explicitly called (lazy loading)
 * Caches results in store to prevent refetching
 */
export const useReferralEarnings = (): UseReferralEarningsReturn => {
  const {
    earnings,
    totalXp,
    totalReferrals,
    hasMore,
    currentOffset,
    isLoading,
    isLoadingMore,
    error,
    _setEarnings,
    _appendEarnings,
    _setTotalXp,
    _setTotalReferrals,
    _setHasMore,
    _setCurrentOffset,
    _setLoading,
    _setLoadingMore,
    _setError,
    _reset,
  } = useReferralEarningsStore();

  /**
   * Initial fetch of referral earnings
   */
  const fetch = useCallback(
    async (isRefetch: boolean = false) => {
      const { isInitialized } = useSDKConfigStore.getState();

      if (!isInitialized) {
        console.error(
          "❌ SDK: Cannot fetch referral earnings - SDK not initialized"
        );
        return;
      }

      // If already has data, don't refetch (cache)
      if (earnings.length > 0 && !isRefetch) {
        console.log("✅ SDK: Using cached referral earnings data");
        return;
      }

      _setLoading(true);
      _setError(null);

      try {
        const response = await getReferralEarnings({
          limit: PAGE_LIMIT,
          offset: 0,
        });

        _setEarnings(response.data.referralEarnings);
        _setTotalXp(response.data.totalXpAmount);
        _setTotalReferrals(response.data.totalReferrals);
        _setHasMore(response.data.hasMore);
        _setCurrentOffset(response.data.referralEarnings.length);

        console.log("✅ SDK: Successfully fetched referral earnings:", {
          count: response.data.referralEarnings.length,
          totalXp: response.data.totalXpAmount,
          totalReferrals: response.data.totalReferrals,
          hasMore: response.data.hasMore,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch referral earnings";
        console.error(
          "❌ SDK: Error fetching referral earnings:",
          errorMessage
        );
        _setError(errorMessage);
      } finally {
        _setLoading(false);
      }
    },
    [
      earnings.length,
      _setLoading,
      _setError,
      _setEarnings,
      _setTotalXp,
      _setTotalReferrals,
      _setHasMore,
      _setCurrentOffset,
    ]
  );

  /**
   * Load more referral earnings (pagination)
   */
  const loadMore = useCallback(async () => {
    const { isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized) {
      console.error(
        "❌ SDK: Cannot load more referral earnings - SDK not initialized"
      );
      return;
    }

    if (!hasMore || isLoadingMore) {
      return;
    }

    _setLoadingMore(true);
    _setError(null);

    try {
      const response = await getReferralEarnings({
        limit: PAGE_LIMIT,
        offset: currentOffset,
      });

      _appendEarnings(response.data.referralEarnings);
      _setHasMore(response.data.hasMore);
      _setCurrentOffset(currentOffset + response.data.referralEarnings.length);

      console.log("✅ SDK: Successfully loaded more referral earnings:", {
        newCount: response.data.referralEarnings.length,
        totalLoaded: currentOffset + response.data.referralEarnings.length,
        hasMore: response.data.hasMore,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to load more referral earnings";
      console.error(
        "❌ SDK: Error loading more referral earnings:",
        errorMessage
      );
      _setError(errorMessage);
    } finally {
      _setLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    currentOffset,
    _setLoadingMore,
    _setError,
    _appendEarnings,
    _setHasMore,
    _setCurrentOffset,
  ]);

  /**
   * Reset referral earnings state (clears cache)
   */
  const reset = useCallback(() => {
    _reset();
  }, [_reset]);

  return {
    earnings,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    totalXp,
    totalReferrals,
    loadMore,
    fetch,
    refetch: () => fetch(true),
    reset,
  };
};
